import * as React from 'react'
import { PaperclipIcon, SendHorizontalIcon } from 'lucide-react'
import { cn } from '../../../lib/utils'

/**
 * Chat message input — text field with attach + send buttons.
 * Submits on Enter (Shift+Enter inserts newline).
 *
 * `onSend` may return (a promise of) `false` to signal a failed send —
 * the input then keeps its content so the user can retry.
 */
export interface ChatInputProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onSubmit'> {
	onSend?: (text: string) => void | boolean | Promise<void | boolean>
	onAttach?: () => void
	wrapperClassName?: string
}

const ChatInput = React.forwardRef<HTMLTextAreaElement, ChatInputProps>(
	({ className, wrapperClassName, onSend, onAttach, placeholder, disabled, ...props }, ref) => {
		const [value, setValue] = React.useState('')

		function send() {
			const text = value.trim()
			if (!text || !onSend || disabled) return
			void Promise.resolve(onSend(text))
				.then((result) => {
					if (result !== false) setValue('')
				})
				// A rejected handler counts as a failed send: keep the content.
				.catch(() => {})
		}

		function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault()
				send()
			}
		}

		// `disabled` reaches the textarea through {...props}, but the two buttons
		// are ours and used to ignore it — a disabled composer still sent on
		// click. They are wired up explicitly.
		return (
			<div
				className={cn(
					'flex items-center gap-2 rounded-(--radius-md) border border-(--color-border) bg-(--color-background) px-3 py-2',
					disabled && 'opacity-60',
					wrapperClassName,
				)}
			>
				<button
					type="button"
					onClick={onAttach}
					disabled={disabled}
					className="shrink-0 text-(--color-muted-foreground) hover:text-(--color-foreground) transition-colors disabled:cursor-not-allowed disabled:hover:text-(--color-muted-foreground)"
					aria-label="Attach file"
				>
					<PaperclipIcon className="size-4" />
				</button>
				<textarea
					ref={ref}
					value={value}
					onChange={(e) => setValue(e.target.value)}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
					disabled={disabled}
					rows={1}
					className={cn(
						'min-h-5 flex-1 resize-none bg-transparent text-sm leading-5 text-(--color-foreground)',
						'placeholder:text-(--color-muted-foreground) focus:outline-none',
						className,
					)}
					{...props}
				/>
				<button
					type="button"
					onClick={send}
					disabled={disabled}
					className="flex size-7 shrink-0 items-center justify-center rounded-(--radius-sm) bg-(--color-secondary) text-(--color-foreground) transition-colors hover:bg-(--color-muted) disabled:cursor-not-allowed disabled:hover:bg-(--color-secondary)"
					aria-label="Send"
				>
					{/* Was a PlusIcon on a button labelled Send. Attaching is the
					    paperclip on the left, so the plus stood for nothing. */}
					<SendHorizontalIcon className="size-4" />
				</button>
			</div>
		)
	}
)

ChatInput.displayName = 'ChatInput'
export { ChatInput }
